import { supabase } from '@/lib/supabase';
import * as Sentry from '@sentry/react';

// A. Costruzione Query Dinamica (fetchClients)
export const getUniqueClientIds = (bookings: any[]): string[] => {
    if (!bookings) return [];
    const ids = bookings
        .filter(b => b.client_id !== null && b.client_id !== undefined)
        .map(b => String(b.client_id));
    return [...new Set(ids)];
};

export const buildFetchClientsQuery = (bookings: any[], organizationId: string) => {
    const uniqueClientIds = getUniqueClientIds(bookings);

    const orConditions: string[] = [];
    if (uniqueClientIds.length > 0) {
        orConditions.push(`id.in.(${uniqueClientIds.join(',')})`);
    }
    orConditions.push(`shop_id.eq.${organizationId}`);

    return orConditions.join(',');
};

export const fetchClientsService = async (bookings: any[], organizationId: string) => {
    const orQuery = buildFetchClientsQuery(bookings, organizationId);

    const { data: clientsData, error } = await supabase
        .from('Customers')
        .select('*')
        .or(orQuery)
        .order('created_at', { ascending: true, nullsFirst: true });

    if (error) {
        throw new Error('Errore nel recupero dei clienti: ' + error.message);
    }

    return clientsData;
};

// B. Trasformazione Dati (Mappers)
export const formatFullName = (firstName?: string, lastName?: string) => {
    let parts = [];
    if (firstName && firstName.trim()) parts.push(firstName.trim());
    if (lastName && lastName.trim()) parts.push(lastName.trim());
    return parts.join(' ');
};

export const createClientService = async (clientData: any, organizationId: string) => {
    try {
        const newClient = {
            full_name: formatFullName(clientData.first_name, clientData.last_name),
            shop_id: organizationId,
            email: clientData.email || null,
            phone: clientData.phone || null,
            is_manually_entry: true,
        };

        const { data, error } = await supabase
            .from('Customers')
            .insert(newClient)
            .select('*')
            .single();

        if (error) {
            throw new Error('Errore nella creazione del cliente');
        }

        return { success: true, data, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: {
                feature: 'clients',
                action: 'create'
            },
            extra: {
                clientData,
                organizationId
            }
        });
        console.error('Errore nella creazione del cliente:', e);
        return { success: false, error: e.message };
    }
};

export const updateClientService = async (id: string, clientData: any, organizationId: string) => {
    try {
        const updateClient = {
            full_name: formatFullName(clientData.first_name, clientData.last_name),
            shop_id: organizationId,
            email: clientData.email || null,
            phone: clientData.phone || null,
            updated_at: new Date().toISOString(),
        };

        const { data, error } = await supabase
            .from('Customers')
            .update(updateClient)
            .eq('id', id)
            .select('*')
            .single();

        if (error) {
            throw new Error("Errore nell'aggiornamento del cliente");
        }

        return { success: true, data, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: {
                feature: 'clients',
                action: 'update'
            },
            extra: {
                id,
                clientData,
                organizationId
            }
        });
        console.error("Errore nell'aggiornamento del cliente:", e);
        return { success: false, error: e.message };
    }
};

// C. Logica di Cancellazione (Soft Delete)
export const getDeletionStrategy = (clientId: string, bookings: any[]) => {
    const hasBookings = bookings?.some(b => b.client_id === clientId);
    return hasBookings ? 'SOFT_DELETE' : 'HARD_DELETE';
};

export const deleteClientService = async (id: string, hasBookings: boolean) => {
    try {
        if (hasBookings) {
            const { data, error } = await supabase
                .from('Customers')
                .update({
                    is_active: false,
                    updated_at: new Date().toISOString(),
                })
                .eq('id', id)
                .select('*')
                .single();

            if (error) {
                throw new Error('Errore nella cancellazione del cliente');
            }

            return { success: true, isSoftDelete: true, data, error: '' };
        }

        const { error } = await supabase
            .from('Customers')
            .delete()
            .eq('id', id);

        if (error) {
            throw new Error('Errore nella cancellazione del cliente');
        }

        return { success: true, isSoftDelete: false, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: {
                feature: 'clients',
                action: 'delete'
            },
            extra: {
                id,
                hasBookings
            }
        });
        console.error('Errore nella cancellazione del cliente:', e);
        return { success: false, error: e.message };
    }
};

// D. Rilevazione Segnalazioni (Customer Reports)
export const createCustomerReportService = async (reportData: { client_id: string; shop_id: number; reason?: string; booking_id?: number }) => {
    try {
        const { data, error } = await supabase
            .from('CustomerReports')
            .insert(reportData)
            .select('*')
            .single();

        if (error) {
            throw new Error('Errore nella creazione della segnalazione: ' + error.message);
        }

        // Chiamata alla Edge Function per l'invio dell'email di notifica segnalazione
        try {
            const { data: functionData, error: functionError } = await supabase.functions.invoke('notify-customer-report', {
                body: { reportId: data.id }
            });

            const errorMessage = functionData?.error || '';

            if (functionError || errorMessage) {
                console.error("Errore Edge Function notify-customer-report:", functionError || errorMessage);
                Sentry.captureException(functionError || new Error(errorMessage), {
                    tags: { feature: 'reports', action: 'report_notification' },
                    extra: { reportId: data.id, response: functionData }
                });
            }
        } catch (err) {
            console.error("Invio notifica segnalazione fallito:", err);
            Sentry.captureException(err, {
                tags: { feature: 'reports', action: 'report_notification_exception' },
                extra: { reportId: data.id }
            });
        }

        return { success: true, data, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: { feature: 'reports', action: 'create' },
            extra: { reportData }
        });
        console.error('Errore nella creazione della segnalazione:', e);
        return { success: false, error: e.message };
    }
};

export const fetchCustomerReportsService = async (shopId: number, clientId?: string) => {
    try {
        let query = supabase
            .from('CustomerReports')
            .select('*')
            .eq('shop_id', shopId)
            .order('created_at', { ascending: false });

        if (clientId) {
            query = query.eq('client_id', clientId);
        }

        const { data, error } = await query;

        if (error) {
            throw new Error('Errore nel recupero delle segnalazioni: ' + error.message);
        }

        return { success: true, data, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: { feature: 'reports', action: 'fetch' },
            extra: { shopId, clientId }
        });
        console.error('Errore nel recupero delle segnalazioni:', e);
        return { success: false, data: null, error: e.message };
    }
};
export const deleteCustomerReportsService = async (shopId: number, clientId: string) => {
    try {
        const { error } = await supabase
            .from('CustomerReports')
            .delete()
            .eq('shop_id', shopId)
            .eq('client_id', clientId);

        if (error) {
            throw new Error('Errore nella cancellazione delle segnalazioni: ' + error.message);
        }

        return { success: true, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: { feature: 'reports', action: 'delete' },
            extra: { shopId, clientId }
        });
        console.error('Errore nella cancellazione delle segnalazioni:', e);
        return { success: false, error: e.message };
    }
};

// E. Gestione Duplicati
export const mergeCustomersService = async (customerIds: string[]) => {
    try {
        const { data, error } = await supabase.rpc('merge_customers', {
            p_customer_ids: customerIds,
        });

        if (error) {
            throw new Error('Errore durante l\'unione dei clienti: ' + error.message);
        }

        return { success: true, masterId: data, error: '' };
    } catch (e: any) {
        Sentry.captureException(e, {
            tags: { feature: 'clients', action: 'merge' },
            extra: { customerIds }
        });
        console.error('Errore nell\'unione dei clienti:', e);
        return { success: false, masterId: null, error: e.message };
    }
};
